import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { Icon } from "@iconify/react";
import { CTADropdown } from "../../components/shared/Dropdown";
import { useGetAllUserCompanies } from "../../queries/auth";
import { useAccountUsers, useIsAccountUser } from "../../queries/companyUsers";
import { AssociatedCompany } from "../../types/UserDetailModel";
import { useAuthStore } from "../../store";
import { Box, HStack } from "../../components/utils";
import { tableCellStyle } from "../../components/TableComponent";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";
import GenericTableHeader from "../../shared/TableHeader";
import SearchInput from "../../components/shared/SearchInput";
import {
  ButtonSize,
  IconCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import SpocTemplateActions from "../onboarding/SpocTemplateActions";
import BulkAssignUsersDrawer, { PickableUser } from "./BulkAssignUsersDrawer";
import AddCompanyModal from "./AddCompanyModal";
import BulkUploadCompanyModal from "./BulkUploadCompanyModal";
import ViewDocumentsModal from "./ViewDocumentsModal";
import RemoveCaptableModal from "./RemoveCaptableModal";
import DeleteCompanyModal from "./DeleteCompanyModal";
import { useGetUserProfile } from "../../queries/userProfile";

type CompanyRow = AssociatedCompany & {
  isCurrent: boolean;
  actions?: any;
};

const verticallyCenteredCellStyle = {
  display: "flex",
  alignItems: "center",
  paddingTop: "0",
};

const verticallyCenteredBoldCellStyle = {
  ...verticallyCenteredCellStyle,
  fontWeight: "600",
};

const verticallyCenteredMutedCellStyle = {
  ...verticallyCenteredCellStyle,
  color: "#a8a29e",
  fontSize: "11px",
};

function BadgeRender({ value, label }: { value: boolean; label: string }) {
  if (!value) return <span className="text-stone-400">-</span>;
  return (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
      {label}
    </span>
  );
}

export default function OnboardedCompanies() {
  const navigate = useNavigate();
  const { data: _companies, isLoading } = useGetAllUserCompanies();
  const companies = _companies || [];
  const { user } = useAuthStore();

  // Onboarding page is only for account-level users; redirect everyone else.
  const { isAccountUser, isLoading: isAccountUserLoading } = useIsAccountUser();
  useEffect(() => {
    if (!isAccountUserLoading && !isAccountUser) navigate("/");
  }, [isAccountUserLoading, isAccountUser, navigate]);

  const gridApi = useRef<GridApi | null>(null);
  const [manageUsersCompanyId, setManageUsersCompanyId] = useState<
    string | null
  >(null);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredRowData, setFilteredRowData] = useState<CompanyRow[]>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyRow[]>([]);
  const [showBulkDrawer, setShowBulkDrawer] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const [viewDocumentsCompanyId, setViewDocumentsCompanyId] = useState<
    string | null
  >(null);
  const [removeCaptableCompanyId, setRemoveCaptableCompanyId] = useState<
    string | null
  >(null);
  const [deleteCompanyIds, setDeleteCompanyIds] = useState<string[]>([]);
  const [editCaptableCompanyId, setEditCaptableCompanyId] = useState<
    string | null
  >(null);

  const currentCompanyId = user?.companyId;

  // De-duplicated pool of account users, used to populate the bulk-assign
  // drawer's pick-and-grant targets (by email). Excludes the logged-in user —
  // granting/revoking your own access through this admin tool doesn't apply.
  const { data: accountUsers } = useAccountUsers();
  const { data: userProfile } = useGetUserProfile();
  const currentUserEmail = (userProfile?.emailAddress || "")
    .trim()
    .toLowerCase();
  const existingUsers = useMemo<PickableUser[]>(() => {
    const byEmail = new Map<string, PickableUser>();
    (accountUsers || []).forEach((u: any) => {
      const emailId = (
        u?.emailId ||
        u?.email ||
        u?.userEmail ||
        ""
      ).toLowerCase();
      if (!emailId || emailId === currentUserEmail) return;
      if (!byEmail.has(emailId)) {
        byEmail.set(emailId, {
          emailId,
          fullName: u?.fullName || u?.name || emailId,
        });
      }
    });
    return Array.from(byEmail.values());
  }, [accountUsers, currentUserEmail]);

  useEffect(() => {
    if (!showAddMenu) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        addMenuRef.current &&
        !addMenuRef.current.contains(e.target as Node)
      ) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddMenu]);

  const onSelectionChanged = useCallback(() => {
    setSelectedCompanies(gridApi.current?.getSelectedRows() || []);
  }, []);

  const clearSelection = useCallback(() => {
    gridApi.current?.deselectAll();
    setSelectedCompanies([]);
  }, []);

  // Opens the assign-users drawer scoped strictly to this one company —
  // no ability to add/remove companies from within it.
  const handleManageUsers = useCallback((company: CompanyRow) => {
    setManageUsersCompanyId(company.uuid);
  }, []);

  const handleViewDocuments = useCallback((company: CompanyRow) => {
    setViewDocumentsCompanyId(company.uuid);
  }, []);

  const handleAttachCaptable = useCallback(
    (company: { uuid: string }) => {
      navigate(`/setting/onboarding/company/${company.uuid}`);
    },
    [navigate]
  );

  const handleRemoveCaptable = useCallback((company: CompanyRow) => {
    setRemoveCaptableCompanyId(company.uuid);
  }, []);

  const handleDeleteCompany = useCallback((company: CompanyRow) => {
    setDeleteCompanyIds([company.uuid]);
  }, []);

  const handleDeleteSelectedCompanies = useCallback(() => {
    setDeleteCompanyIds(selectedCompanies.map((company) => company.uuid));
  }, [selectedCompanies]);

  const handleEditCaptable = useCallback((company: CompanyRow) => {
    setEditCaptableCompanyId(company.uuid);
  }, []);

  const manageUsersCompany = companies.find(
    (c) => c.uuid === manageUsersCompanyId
  );
  const viewDocumentsCompany = companies.find(
    (c) => c.uuid === viewDocumentsCompanyId
  );
  const removeCaptableCompany = companies.find(
    (c) => c.uuid === removeCaptableCompanyId
  );
  const deleteCompanies = companies.filter((c) =>
    deleteCompanyIds.includes(c.uuid)
  );
  const editCaptableCompany = companies.find(
    (c) => c.uuid === editCaptableCompanyId
  );
  // The backend only populates `users` on /userCompanies for companies where
  // the caller is an Admin, and the shape is { id, fullName, email, role }
  // (not { userId, emailId, roleId } — see companiesStats.getCompanyUsers).
  const accessibleUsers = useMemo(
    () =>
      (manageUsersCompany?.users || [])
        .map((u: any) => ({
          userId: u?.id ?? u?.userId,
          emailId: u?.email || u?.emailId || u?.userEmail || "",
          fullName: u?.fullName || u?.name || "",
        }))
        .filter((u) => u.emailId && u.userId != null),
    [manageUsersCompany]
  );

  const openToolPanel = (key: "columns" | "filters") => {
    if (!gridApi.current) return;
    if (key === "columns") {
      if (!isColumnOpen) gridApi.current.openToolPanel(key);
      else gridApi.current.closeToolPanel();
      setIsColumnOpen((s) => !s);
      setIsFilterOpen(false);
    } else {
      if (!isFilterOpen) gridApi.current.openToolPanel(key);
      else gridApi.current.closeToolPanel();
      setIsFilterOpen((s) => !s);
      setIsColumnOpen(false);
    }
  };

  const onAgGridFilterChanged = (grid: any) => {
    setIsFilterApplied(grid.api.isAnyFilterPresent());
    setFilteredRowData(
      grid.api.getModel().rowsToDisplay.map((node: any) => node.data)
    );
  };

  const rowData: CompanyRow[] = useMemo(
    () =>
      companies.map((company) => {
        const row: CompanyRow = {
          ...company,
          isCurrent: company.uuid === currentCompanyId,
        };
        const hasDocuments = (company.companyDocuments?.length ?? 0) > 0;
        const canAttachCaptable =
          company.isTaskManagerCompany && company.isDraftCompany;
        const canRemoveCaptable =
          company.isTaskManagerCompany && !company.isDraftCompany;
        row.actions = (
          <CTADropdown
            portal
            actions={[
              { name: "Manage Users", disabled: false },
              ...(hasDocuments
                ? [{ name: "View Documents", disabled: false }]
                : []),
              ...(canAttachCaptable
                ? [{ name: "Attach Captable", disabled: false }]
                : []),
              ...(canRemoveCaptable
                ? [{ name: "Remove Captable", disabled: false }]
                : []),
              ...(company.isTaskManagerCompany === true
                ? [
                    { name: "Edit cap table", disabled: false },
                    { name: "Delete Company", disabled: false },
                  ]
                : []),
            ]}
            onAction={(action) => {
              if (action.name === "Manage Users") handleManageUsers(row);
              if (action.name === "View Documents") handleViewDocuments(row);
              if (action.name === "Attach Captable") handleAttachCaptable(row);
              if (action.name === "Remove Captable") handleRemoveCaptable(row);
              if (action.name === "Edit cap table") handleEditCaptable(row);
              if (action.name === "Delete Company") handleDeleteCompany(row);
            }}
          />
        );
        return row;
      }),
    [
      companies,
      currentCompanyId,
      handleManageUsers,
      handleViewDocuments,
      handleAttachCaptable,
      handleRemoveCaptable,
      handleEditCaptable,
      handleDeleteCompany,
    ]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      autoHeight: false,
      wrapHeaderText: true,
      minWidth: 120,
      flex: 1,
      cellStyle: verticallyCenteredCellStyle,
      menuTabs: ["filterMenuTab"],
      filterParams: {
        buttons: ["reset"],
      },
    }),
    []
  );

  const columnDefs = useMemo<ColDef<CompanyRow>[]>(
    () => [
      {
        headerName: "Company Name",
        field: "nameOfTheCompany",
        filter: "agTextColumnFilter",
        pinned: "left",
        minWidth: 240,
        tooltipField: "nameOfTheCompany",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellStyle: verticallyCenteredBoldCellStyle,
        cellRenderer: (p: ICellRendererParams<CompanyRow>) => (
          <div className="flex flex-col justify-center gap-0.5 py-1">
            <span>{p.value}</span>
            {p.data?.isCurrent && (
              <span className="w-fit rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium leading-none text-primary">
                Current
              </span>
            )}
          </div>
        ),
      },
      {
        headerName: "CIN",
        field: "cin",
        filter: "agTextColumnFilter",
        minWidth: 200,
        valueFormatter: (p) => p.value || "-",
      },
      // {
      //   headerName: "Currency",
      //   field: "companyCurrency",
      //   filter: "agSetColumnFilter",
      //   valueFormatter: (p) => p.value || "-",
      // },
      {
        headerName: "Jurisdiction",
        field: "jurisdiction",
        filter: "agSetColumnFilter",
        valueFormatter: (p) => p.value || "-",
      },
      {
        headerName: "Valuation",
        field: "valuation",
        filter: "agNumberColumnFilter",
        valueFormatter: (p) =>
          typeof p.value === "number" ? p.value.toLocaleString() : "-",
        hide: true,
      },
      {
        headerName: "Share Price",
        field: "sharePrice",
        filter: "agNumberColumnFilter",
        valueFormatter: (p) =>
          typeof p.value === "number" ? p.value.toLocaleString() : "-",
        hide: true,
      },
      {
        headerName: "Company ID",
        field: "uuid",
        filter: "agTextColumnFilter",
        minWidth: 280,
        cellStyle: verticallyCenteredMutedCellStyle,
      },
      // {
      //   headerName: "Default",
      //   field: "isDefaultCompany",
      //   filter: "agSetColumnFilter",
      //   cellRenderer: (p: ICellRendererParams<CompanyRow>) => (
      //     <BadgeRender value={Boolean(p.value)} label="Default" />
      //   ),
      // },
      // {
      //   headerName: "Status",
      //   field: "isCurrent",
      //   filter: "agSetColumnFilter",
      //   cellRenderer: (p: ICellRendererParams<CompanyRow>) =>
      //     p.value ? (
      //       <span className="rounded-full bg-primary/10 px-2
      //  py-0.5 text-xs font-medium text-primary">
      //         Current
      //       </span>
      //     ) : (
      //       <span className="text-stone-400">-</span>
      //     ),
      // },
      {
        headerName: "",
        field: "actions",
        colId: "action-column",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          lineHeight: "20px",
        },
        resizable: false,
        sortable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        cellRenderer: CTADropdown,
      },
    ],
    []
  );

  if (!isAccountUserLoading && !isAccountUser) return null;

  return (
    <>
      <HStack className="mb-2 gap-2 justify-end">
        <button
          type="button"
          className="whitespace-nowrap rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          onClick={() => navigate("/setting/external-captable")}
        >
          External cap table
        </button>
        {/* Stage 3 AI onboarding is temporarily disabled.
        <button
          type="button"
          className={
            "whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-white"
          }
          onClick={() => navigate("/company-onboarding")}
        >
          Add multiple companies
        </button>
        <button
          type="button"
          className={
            "whitespace-nowrap rounded-md border border-primary px-4 py-2 " +
            "text-sm font-medium text-primary hover:bg-primary/5"
          }
          onClick={() =>
            navigate("/setting/captable-ai/jobs", {
              state: { returnTo: "/setting/users" },
            })
          }
        >
          Check processing status
        </button> */}
      </HStack>
      <HStack className="justify-between py-2 items-center bg-white rounded-md">
        <GenericTableHeader
          heading={"Onboarded Companies"}
          subHeading={"Companies"}
          count={isFilterApplied ? filteredRowData.length : rowData.length}
        />
        <HStack className="justify-end py-2 items-center gap-4 px-8">
          <SearchInput
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current?.setQuickFilter(e.target.value);
            }}
          />
          <div className="relative" ref={addMenuRef}>
            <PrimaryCTAButton
              event-name="Open Add Company Menu"
              buttonSize={ButtonSize.MEDIUM}
              onClick={() => setShowAddMenu((s) => !s)}
            >
              <span className="flex items-center gap-1">
                Add Company
                <Icon icon="mdi:chevron-down" height={16} />
              </span>
            </PrimaryCTAButton>
            {showAddMenu && (
              <div
                className={
                  "absolute right-0 z-10 mt-1 w-full rounded-md border " +
                  "border-stone-200 bg-white py-1 shadow-lg"
                }
              >
                <button
                  type="button"
                  data-event-name="Open Add Company"
                  className={
                    "block w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50"
                  }
                  onClick={() => {
                    setShowAddMenu(false);
                    setShowAddCompany(true);
                  }}
                >
                  Add Company
                </button>
                <button
                  type="button"
                  data-event-name="Open Bulk Upload Company"
                  className={
                    "block w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50"
                  }
                  onClick={() => {
                    setShowAddMenu(false);
                    setShowBulkUpload(true);
                  }}
                >
                  Bulk Upload
                </button>
              </div>
            )}
          </div>
          <div className="self-end mt-2">
            <SpocTemplateActions />
          </div>
          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4 font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
        </HStack>
      </HStack>
      {selectedCompanies.length > 1 && (
        <HStack className="items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 mb-2">
          <HStack className="items-center gap-3">
            <span className="text-sm font-medium text-stone-700">
              {selectedCompanies.length}{" "}
              {selectedCompanies.length === 1 ? "company" : "companies"}{" "}
              selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs font-medium text-stone-500 underline hover:text-stone-700"
            >
              Clear
            </button>
          </HStack>
          <HStack className="items-center gap-3">
            <PrimaryCTAButton
              event-name="Open Bulk Assign"
              onClick={() => setShowBulkDrawer(true)}
            >
              Assign users
            </PrimaryCTAButton>
            <PrimaryCTAButton
              event-name="Open Bulk Delete"
              onClick={handleDeleteSelectedCompanies}
            >
              Delete companies
            </PrimaryCTAButton>
          </HStack>
        </HStack>
      )}
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: `${
              (rowData.length >= 10 ? 10 : rowData.length + 3) * 58
            }px`,
          }}
          className="onboarded-companies-grid w-full max-h-full overflow-x-auto ag-theme-material h-full"
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              gridApi.current = params.api;
            }}
            popupParent={document.body}
            animateRows={true}
            defaultColDef={defaultColDef}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={false}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            onSelectionChanged={onSelectionChanged}
            suppressRowTransform={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            onFilterChanged={onAgGridFilterChanged}
            tooltipShowDelay={1000}
            rowClass={"border-t border-dashed"}
            getRowHeight={(params) => (params.data?.isCurrent ? 82 : 58)}
            overlayNoRowsTemplate={
              isLoading
                ? '<span style="padding: 10px;">Loading companies...</span>'
                : '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No onboarded companies found</span>'
            }
            getRowStyle={(params) =>
              (params.rowIndex ?? 0) % 2 === 0
                ? { background: "#f8f8f8" }
                : { background: "#ffffff" }
            }
          ></AgGridReact>
        </Box>
      </HStack>

      {/* Per-row "Manage Users" — strictly scoped to the selected company */}
      <BulkAssignUsersDrawer
        open={Boolean(manageUsersCompany)}
        companies={
          manageUsersCompany
            ? [
                {
                  uuid: manageUsersCompany.uuid,
                  nameOfTheCompany: manageUsersCompany.nameOfTheCompany,
                },
              ]
            : []
        }
        allCompanies={[]}
        existingUsers={existingUsers}
        accessibleUsers={accessibleUsers}
        lockCompanies
        onClose={() => setManageUsersCompanyId(null)}
        onDone={() => setManageUsersCompanyId(null)}
      />

      <BulkAssignUsersDrawer
        open={showBulkDrawer}
        companies={selectedCompanies.map((c) => ({
          uuid: c.uuid,
          nameOfTheCompany: c.nameOfTheCompany,
        }))}
        allCompanies={companies.map((c) => ({
          uuid: c.uuid,
          nameOfTheCompany: c.nameOfTheCompany,
        }))}
        existingUsers={existingUsers}
        onClose={() => setShowBulkDrawer(false)}
        onDone={clearSelection}
      />

      <AddCompanyModal
        open={showAddCompany}
        onClose={() => setShowAddCompany(false)}
      />

      <AddCompanyModal
        open={Boolean(editCaptableCompany)}
        company={editCaptableCompany}
        onClose={() => setEditCaptableCompanyId(null)}
      />

      <BulkUploadCompanyModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
      />

      <ViewDocumentsModal
        open={Boolean(viewDocumentsCompany)}
        company={
          viewDocumentsCompany
            ? {
                uuid: viewDocumentsCompany.uuid,
                nameOfTheCompany: viewDocumentsCompany.nameOfTheCompany,
              }
            : null
        }
        documents={viewDocumentsCompany?.companyDocuments ?? []}
        onClose={() => setViewDocumentsCompanyId(null)}
      />

      <RemoveCaptableModal
        open={Boolean(removeCaptableCompany)}
        company={
          removeCaptableCompany
            ? {
                uuid: removeCaptableCompany.uuid,
                nameOfTheCompany: removeCaptableCompany.nameOfTheCompany,
              }
            : null
        }
        documents={removeCaptableCompany?.companyDocuments ?? []}
        onClose={() => setRemoveCaptableCompanyId(null)}
        onReupload={() => {
          if (!removeCaptableCompany) return;
          setRemoveCaptableCompanyId(null);
          handleAttachCaptable(removeCaptableCompany);
        }}
      />

      <DeleteCompanyModal
        open={deleteCompanies.length > 0}
        companies={deleteCompanies.map((company) => ({
          uuid: company.uuid,
          nameOfTheCompany: company.nameOfTheCompany,
        }))}
        onClose={() => {
          setDeleteCompanyIds([]);
          clearSelection();
        }}
      />
    </>
  );
}
